/**
 * Route policy for a seatless member of a capped trial workspace.
 *
 * Pure and separate from the component so the matching rule is testable — a
 * bare `startsWith` here silently mis-classifies both `/documentation` (exact)
 * and `/settings-anything` (unrelated sibling), and either mistake is invisible
 * until someone lands on the wrong page.
 */

/**
 * Routes a seatless person keeps.
 *
 * `/settings` stays open because billing lives there — shutting it would leave
 * someone unable to upgrade out of the very state that locked them. `/home` is
 * where the redirect lands them, and `/documentation` is content the dashboard
 * links to directly.
 */
export const TRIAL_ALLOWED_ROUTES = ['/home', '/settings', '/documentation'];

/**
 * Allowed routes where the explanation must NOT render as a blocking dialog.
 *
 * The dialog draws a fixed full-viewport backdrop, so showing it on `/settings`
 * would make the page unusable — defeating the reason `/settings` is allowed at
 * all. The reduced sidebar already signals the state on these routes.
 */
export const TRIAL_DIALOG_FREE_ROUTES = ['/settings', '/documentation'];

/**
 * Whether `pathname` is one of `routes` or a descendant of one.
 *
 * Matches the exact route or a slash-delimited child, never a bare string
 * prefix: `/documentation` and `/documentation/setup` both match
 * `/documentation`, while `/settings-export` does not match `/settings`.
 *
 * Args:
 *   pathname: Current path, e.g. `/documentation/setup`.
 *   routes: Route roots to test against.
 *
 * Returns:
 *   True when the path is covered by one of the routes.
 *
 * Example:
 *   matchesTrialRoute('/documentation', TRIAL_ALLOWED_ROUTES) // true
 */
export const matchesTrialRoute = (
  pathname: string,
  routes: string[]
): boolean =>
  routes.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`)
  );
